import { Box, IconButton, Menu, Stack } from '@mui/material'
import React, { useRef } from 'react'
import { useLocation } from 'react-router'
import { useBoolean } from 'react-use'

import Icon from 'components/Icon'
import {
  getTeamSwitchRedirectUrl,
  MAIN_HEADER_MENU_WIDTH
} from 'components/navigation/menu/main-header-menu-utils'
import { MainMenuPanel } from 'components/navigation/menu/MainMenuPanel'
import { TeamSelectionMenuPanel } from 'components/navigation/menu/TeamSelectionMenuPanel'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { muiTheme } from 'mui-theme'
import { track } from 'track'

export const MainHeaderMenu: React.FC = () => {
  const { currentTeam, session, setActiveTeamId } = useAuth()
  const location = useLocation()

  const [isTeamSwitcherVisible, setIsTeamSwitcherVisible] = useBoolean(false)
  const [isOpen, setIsOpen] = useBoolean(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleOpenMenu = () => {
    setIsOpen(true)
  }

  const handleCloseMenu = () => {
    setIsOpen(false)

    if (isTeamSwitcherVisible) {
      setIsTeamSwitcherVisible(false)
    }
  }

  const handleShowTeamSelectionMenuPanel = () => {
    setIsTeamSwitcherVisible(true)
  }

  const handleHideTeamSelectionMenuPanel = () => {
    setIsTeamSwitcherVisible(false)
  }

  const handleSelectActiveTeam = async (team) => {
    handleCloseMenu()

    await setActiveTeamId(team.identifier)

    track('Team Selected', {
      category: 'MainHeaderMenu'
    })

    window.location.href = getTeamSwitchRedirectUrl(team, location)

    toast({
      type: 'success',
      text: `Successfully switched to team: ${team.name}`,
      testKey: team.name
    })
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label="Main Header Menu Button"
        data-testid="main-header-menu-button"
        sx={{
          backgroundColor: isOpen
            ? muiTheme.palette.common.white
            : muiTheme.palette.grey['900'],
          border: `1px solid ${muiTheme.palette.divider}`,
          borderRadius: '100%',
          color: isOpen
            ? muiTheme.palette.primary.main
            : muiTheme.palette.primary.contrastText,
          borderColor: isOpen
            ? muiTheme.palette.divider
            : muiTheme.palette.grey['900'],

          '&:hover': {
            backgroundColor: muiTheme.palette.primary.contrastText,
            borderColor: muiTheme.palette.divider,
            color: muiTheme.palette.primary.main
          }
        }}
        onClick={handleOpenMenu}
      >
        <Icon icon="gear" />
      </IconButton>
      <Menu
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        aria-label="Main Header Menu Panel"
        open={isOpen}
        sx={{ width: MAIN_HEADER_MENU_WIDTH, mt: 1 }}
        onClose={handleCloseMenu}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{
            transform: isTeamSwitcherVisible
              ? 'translateX(-100%)'
              : 'translateX(0)',
            transition: 'transform ease 300ms'
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              p: 1,
              maxHeight: 450
            }}
          >
            <MainMenuPanel
              currentTeam={currentTeam}
              session={session}
              onShowTeamSelectionMenuPanel={handleShowTeamSelectionMenuPanel}
            />
          </Box>
          <Stack
            sx={{
              position: 'relative',
              minWidth: '100%',
              p: 2,
              maxHeight: 450
            }}
          >
            <TeamSelectionMenuPanel
              onHideTeamSelectionMenuPanel={handleHideTeamSelectionMenuPanel}
              onSelectActiveTeam={handleSelectActiveTeam}
            />
          </Stack>
        </Stack>
      </Menu>
    </>
  )
}
